import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Activity, Check, LogOut, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const FEATURES = {
  starter: [
    "Real-time KPI dashboard",
    "Interactive charts, gauges & sparklines",
    "Global + widget-level filters",
    "Up to 3 saved dashboards",
    "CSV export",
  ],
  pro: [
    "Everything in Starter",
    "Drag-and-drop custom layouts",
    "Threshold alerts → Slack & email",
    "Scheduled PDF/CSV reports",
    "Role-based shareable links",
    "Priority data streaming",
  ],
};

export default function Pricing() {
  const { user, subscription, logout, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  const hasAccess = subscription?.has_access;
  const activePlan = subscription?.plan;
  const status = subscription?.status;

  const subscribe = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const cancel = async () => {
    setCanceling(true);
    try {
      await api.post("/subscription/cancel");
      await refreshSubscription();
      toast.success("Subscription will cancel at period end");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setCanceling(false);
    }
  };

  const meta = { starter: FEATURES.starter, pro: FEATURES.pro };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <Activity className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold tracking-tight">PULSE / KPI</span>
          </div>
          <div className="flex items-center gap-3">
            {user && <span className="text-sm text-muted-foreground hidden sm:block">{user.email}</span>}
            {hasAccess && <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} data-testid="go-dashboard-button">Go to dashboard</Button>}
            <button onClick={logout} className="h-9 w-9 flex items-center justify-center rounded-md border border-border hover:bg-secondary hover:text-destructive transition-colors" data-testid="pricing-logout-button" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Choose your plan · 7-day free trial</span>
        </div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight max-w-2xl leading-tight">
          Unlock your command center.
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl">
          {hasAccess
            ? "You're all set. Manage your plan below."
            : "Start a 7-day free trial. Cancel anytime before it ends and you won't be charged."}
        </p>

        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {["starter", "pro"].map((id) => {
            const plan = plans.find((p) => p.id === id);
            const isCurrent = hasAccess && activePlan === id;
            const highlight = id === "pro";
            return (
              <div
                key={id}
                className={`relative rounded-lg border p-6 flex flex-col ${highlight ? "border-primary bg-primary/5" : "border-border bg-card"}`}
                data-testid={`plan-card-${id}`}
              >
                {highlight && (
                  <span className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-widest bg-primary text-primary-foreground px-2 py-0.5 rounded">Most popular</span>
                )}
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-xl font-bold tracking-tight">{plan?.name || (id === "pro" ? "PULSE Pro" : "PULSE Starter")}</h3>
                  {isCurrent && <Badge variant="secondary" className="text-[10px] uppercase" data-testid={`current-badge-${id}`}>{status}</Badge>}
                </div>
                <div className="mt-4 mb-6 flex items-end gap-1">
                  <span className="tnum text-4xl font-bold tracking-tight">${plan?.price ?? (id === "pro" ? 49 : 19)}</span>
                  <span className="text-muted-foreground mb-1 text-sm">/month</span>
                </div>
                <ul className="space-y-2.5 flex-1">
                  {meta[id].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${highlight ? "text-primary" : "text-[hsl(142_71%_55%)]"}`} />
                      <span className="text-foreground/90">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {isCurrent ? (
                    subscription?.cancel_at_period_end ? (
                      <Button className="w-full" variant="outline" disabled data-testid={`canceling-${id}`}>Cancels at period end</Button>
                    ) : (
                      <Button className="w-full" variant="outline" onClick={cancel} disabled={canceling} data-testid={`cancel-${id}`}>
                        {canceling ? "Canceling…" : "Cancel subscription"}
                      </Button>
                    )
                  ) : (
                    <Button
                      className="w-full group"
                      variant={highlight ? "default" : "outline"}
                      onClick={() => subscribe(id)}
                      disabled={!!loadingPlan || (hasAccess && !isCurrent)}
                      data-testid={`subscribe-${id}`}
                    >
                      {loadingPlan === id ? "Redirecting…" : hasAccess ? "Switch plan" : "Start free trial"}
                      <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-8 font-mono">Secure checkout by Stripe · test card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </div>
    </div>
  );
}
